import React from 'react';
import styles from 'src/components/atomic/molecules/transactionsOverview/transactionsOverview.module.scss';
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import colors from 'src/styles/colors.module.scss';
import Selectors from 'src/utils/selectors';
import LiteralsService from 'src/services/literals/LiteralsService';
import SyncLoader from 'src/components/sync-loader/SyncLoader';
import { LoaderType } from 'src/types/loader-type.enum';
import FormatService from 'src/services/format.service';
import usePaginatedTransactions from 'src/hooks/usePaginatedTransactions';
import { useAccountSyncStatuses } from 'src/hooks/queries/accounts';
import AccountService from 'src/services/account.service';
import { useBreakpoints } from 'src/hooks/useBreakpoints';
import { useFeatureFlag } from 'src/hooks/queries/one-xp';
import oneXP from 'src/types/one-xp';

export enum TransactionsOverviewVersions {
    AUTO = 'auto', // Choose automatically based on feature flag value
    TRANSFERS_IN_OUT = 'transfersInOut',
    ORIGINAL = 'original'
}

export interface TransactionsOverviewProps {
    version?: TransactionsOverviewVersions;
}

export interface TransactionsOverviewDataProps {
    isLoadingPaginatedTransactions?: boolean;
    isSyncingAccounts?: boolean;
    income?: number;
    expense?: number;
    transferIn?: number;
    transferOut?: number;
    totalLength?: number;
    isMinWidthSmall?: boolean;
    isMinWidthMedium?: boolean;
}

function Original({
    isLoadingPaginatedTransactions = false,
    isSyncingAccounts = false,
    income,
    expense,
    totalLength = 0,
    isMinWidthSmall = false
}: TransactionsOverviewDataProps): JSX.Element {
    if (isLoadingPaginatedTransactions) return null;
    const titleVariant: TypographyVariants = !isMinWidthSmall ? TypographyVariants.CAPTION : TypographyVariants.SUBTEXT;
    const titleClassName: string = !isMinWidthSmall ? styles.weak : '';

    return (
        <div className={styles.overviewSection}>
            <Typography
                variant={TypographyVariants.HEADING_L}
                className={colors.brandStrong}
                aria-live="polite"
                data-cy={Selectors.Transactions.NO_OF_TRANSACTIONS}
            >
                {LiteralsService.getLiteral('TransactionsView.noOfTransactions', [totalLength])}
            </Typography>
            <div className={styles.figuresSection}>
                {(income || isSyncingAccounts) && (
                    <div>
                        <Typography component="p" variant={titleVariant} className={titleClassName}>
                            {LiteralsService.getLiteral('TransactionsView.incomeOverview')}
                        </Typography>
                        <SyncLoader type={LoaderType.PILL}>
                            <Typography
                                component="p"
                                variant={TypographyVariants.HEADING_XS}
                                data-cy={Selectors.Transactions.TOTAL_INCOME}
                            >
                                {FormatService.formatCurrency(income)}
                            </Typography>
                        </SyncLoader>
                    </div>
                )}
                {(expense || isSyncingAccounts) && (
                    <div>
                        <Typography component="p" variant={titleVariant} className={titleClassName}>
                            {LiteralsService.getLiteral('TransactionsView.expensesOverview')}
                        </Typography>
                        <SyncLoader type={LoaderType.PILL}>
                            <Typography
                                component="p"
                                variant={TypographyVariants.HEADING_XS}
                                data-cy={Selectors.Transactions.TOTAL_EXPENSES}
                            >
                                {FormatService.formatCurrency(expense)}
                            </Typography>
                        </SyncLoader>
                    </div>
                )}
            </div>
        </div>
    );
}

function TransfersInOutVersion({
    isLoadingPaginatedTransactions = false,
    isSyncingAccounts = false,
    income,
    expense,
    totalLength = 0,
    transferIn,
    transferOut,
    isMinWidthSmall = false,
    isMinWidthMedium = false
}: TransactionsOverviewDataProps): JSX.Element {
    if (isLoadingPaginatedTransactions) return null;
    const titleVariant: TypographyVariants = !isMinWidthSmall ? TypographyVariants.CAPTION : TypographyVariants.SUBTEXT;
    const titleClassName: string = !isMinWidthSmall ? styles.weak : '';

    const getIncomeMarkup = (): JSX.Element => (
        <div>
            <SyncLoader type={LoaderType.PILL}>
                <Typography
                    component="p"
                    variant={TypographyVariants.HEADING_XS}
                    data-cy={Selectors.Transactions.TOTAL_INCOME}
                >
                    {FormatService.formatCurrency(income)}
                </Typography>
            </SyncLoader>
            <Typography component="p" variant={titleVariant} className={titleClassName}>
                {LiteralsService.getLiteral('TransactionsView.incomeOverview')}
            </Typography>
        </div>
    );

    const getExpenseMarkup = (): JSX.Element => (
        <div>
            <SyncLoader type={LoaderType.PILL}>
                <Typography
                    component="p"
                    variant={TypographyVariants.HEADING_XS}
                    data-cy={Selectors.Transactions.TOTAL_EXPENSES}
                >
                    {FormatService.formatCurrency(expense)}
                </Typography>
            </SyncLoader>
            <Typography component="p" variant={titleVariant} className={titleClassName}>
                {LiteralsService.getLiteral('TransactionsView.expensesOverview')}
            </Typography>
        </div>
    );

    const getTransferInMarkup = (className: string = ''): JSX.Element => (
        <div className={className}>
            <SyncLoader type={LoaderType.PILL}>
                <Typography
                    component="p"
                    variant={TypographyVariants.HEADING_XS}
                    data-cy={Selectors.Transactions.TOTAL_TRANSFER_IN}
                >
                    {FormatService.formatCurrency(transferIn)}
                </Typography>
            </SyncLoader>
            <Typography component="p" variant={titleVariant} className={titleClassName}>
                {LiteralsService.getLiteral('TransactionsView.transferInOverview')}
            </Typography>
        </div>
    );

    const getTransferOutMarkup = (className: string = ''): JSX.Element => (
        <div className={className}>
            <SyncLoader type={LoaderType.PILL}>
                <Typography
                    component="p"
                    variant={TypographyVariants.HEADING_XS}
                    data-cy={Selectors.Transactions.TOTAL_TRANSFER_OUT}
                >
                    {FormatService.formatCurrency(transferOut)}
                </Typography>
            </SyncLoader>
            <Typography component="p" variant={titleVariant} className={titleClassName}>
                {LiteralsService.getLiteral('TransactionsView.transferOutOverview')}
            </Typography>
        </div>
    );

    return (
        <div className={styles.overviewSection}>
            <Typography
                variant={TypographyVariants.HEADING_L}
                className={colors.brandStrong}
                aria-live="polite"
                data-cy={Selectors.Transactions.NO_OF_TRANSACTIONS}
            >
                {LiteralsService.getLiteral('TransactionsView.noOfTransactions', [totalLength])}
            </Typography>
            <div className={styles.figuresSection}>
                {(income || isSyncingAccounts) && (
                    <div>
                        {!isMinWidthMedium &&
                            (transferIn !== 0 || transferOut !== 0) &&
                            getTransferInMarkup(styles.transferMargin)}
                        {getIncomeMarkup()}
                    </div>
                )}
                {(expense || isSyncingAccounts) && (
                    <div>
                        {!isMinWidthMedium &&
                            (transferIn !== 0 || transferOut !== 0) &&
                            getTransferOutMarkup(styles.transferMargin)}
                        {getExpenseMarkup()}
                    </div>
                )}
                {isMinWidthMedium && (transferIn !== 0 || transferOut !== 0) && (
                    <>
                        {(!!expense || !!income) && <div className={styles.verticalDivider} />}
                        <div>{getTransferInMarkup()}</div>
                        <div>{getTransferOutMarkup()}</div>
                    </>
                )}
            </div>
        </div>
    );
}

export function TransactionsOverviewComponent({
    externalProps,
    data
}: {
    externalProps: TransactionsOverviewProps;
    data: TransactionsOverviewDataProps;
}): JSX.Element {
    if (externalProps.version === TransactionsOverviewVersions.TRANSFERS_IN_OUT) {
        return <TransfersInOutVersion {...data} />;
    }
    return <Original {...data} />;
}

export default function TransactionsOverview({
    version = TransactionsOverviewVersions.AUTO
}: TransactionsOverviewProps): JSX.Element {
    const { isLoading: isLoadingPaginatedTransactions, data: paginatedTransactions } = usePaginatedTransactions();
    const { data: syncStatuses } = useAccountSyncStatuses();
    const isSyncingAccounts = AccountService.isSyncingAccounts(syncStatuses);
    const { isMinWidthSmall, isMinWidthMedium } = useBreakpoints();
    const { data: transfersInTransfersOutFeatureFlag } = useFeatureFlag(
        oneXP.FeatureFlags.TransfersInTransfersOutTransactionSummary
    );

    const data: TransactionsOverviewDataProps = {
        isLoadingPaginatedTransactions,
        isSyncingAccounts,
        income: paginatedTransactions?.income,
        expense: paginatedTransactions?.expenses,
        totalLength: paginatedTransactions?.totalLength || 0,
        transferIn: paginatedTransactions?.transferIn,
        transferOut: paginatedTransactions?.transferOut,
        isMinWidthSmall,
        isMinWidthMedium
    };

    const externalProps: TransactionsOverviewProps = {
        version:
            version !== TransactionsOverviewVersions.AUTO
                ? version
                : transfersInTransfersOutFeatureFlag
                ? TransactionsOverviewVersions.TRANSFERS_IN_OUT
                : TransactionsOverviewVersions.ORIGINAL
    };

    return <TransactionsOverviewComponent externalProps={externalProps} data={data} />;
}
